import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useIsPresent } from 'motion/react';
import { type PropsWithChildren, type ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { hasOpenedPopover } from '../../utils/ui';
import { emphasizedTransition } from '../../constants/ui';
import { useTransitionMark } from '../../hooks/use-transition-mark';

import styles from './overlay-sidebar.module.scss';

const overlaySidebarTransition = { ...emphasizedTransition, type: 'tween' } as const;

const overlaySidebarVariants = {
  open: {
    x: -20,
    transition: overlaySidebarTransition,
  },
  closed: {
    x: '100%',
    transition: overlaySidebarTransition,
  },
} as const;

export const OverlaySidebar = (props: OverlaySidebarProps) => {
  const { open, loader, children, onClickOutside, onClose, closeOnClickOutside = true } = props;
  const [animationComplete, setAnimationComplete] = useState(false);

  useLayoutEffect(() => {
    open && setAnimationComplete(false);
  }, [open]);

  return createPortal(
    <AnimatePresence onExitComplete={() => setAnimationComplete(false)}>
      {open && (
        <motion.div key="overlay-sidebar" className={styles.overlaySidebarHolder}>
          <OverlaySidebarBackdrop
            onClick={() => {
              onClickOutside?.();
              closeOnClickOutside && !hasOpenedPopover() && onClose?.();
            }}
          />
          <motion.div
            className={styles.overlaySidebar}
            variants={overlaySidebarVariants}
            initial="closed"
            animate="open"
            exit="closed"
            onAnimationComplete={(definition) => {
              if (definition === 'open') {
                setAnimationComplete(true);
              }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {animationComplete || !loader ? children : loader}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

// A CSS opacity transition rather than motion: its WAAPI opacity animation over the `backdrop-filter`
// makes Chrome present stale frames, popping the blur to full or zero strength (DM-1559).
const OverlaySidebarBackdrop = ({ onClick }: { onClick: () => void }) => {
  const ref = useRef<HTMLDivElement>(null);
  // Not present once the sidebar starts closing; the panel's exit slide keeps the backdrop mounted while it fades out.
  useTransitionMark(ref, 'open', useIsPresent());

  return <div ref={ref} data-testid="overlay-sidebar" className={styles.overlaySidebarBackdrop} onClick={onClick} />;
};

export interface OverlaySidebarProps extends PropsWithChildren {
  open: boolean;
  loader?: ReactNode;
  onClose?: () => void;
  onClickOutside?: () => void;
  closeOnClickOutside?: boolean;
}
